/**
 * Pantalla genérica del módulo **Bonus**: el historial de solicitudes de una
 * operación contra el servicio de Primax.
 *
 * Las cuatro pantallas del panel anterior (`Bonus::transaction`, `balance`,
 * `accumulate` y `exchange`) eran el mismo DataTable de `bonus_request` con un
 * `request_url` distinto; aquí se comparte la plantilla y cada ruta aporta su
 * título, descripción y tipo.
 */

import type { LucideIcon } from 'lucide-react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { formatDateTimeValue } from '@/lib/utils';

import { useSolicitudesBonus } from '../bonus.api';
import { CuerpoJson } from '../components/cuerpo-json';

export interface BonusSolicitudesPageProps {
  /** Título corto, sin el prefijo «Bonus». */
  titulo: string;
  /** Rótulo superior (la sección a la que pertenece). */
  eyebrow: string;
  descripcion: string;
  /** `request_url` que filtra el listado (ver `TIPOS_BONUS`). */
  tipo: string;
  /** Muestra el bloque «Datos enviados» (Balance no lo tenía en el panel). */
  mostrarEnviados: boolean;
  icono: LucideIcon;
}

export function BonusSolicitudesPage({
  titulo,
  eyebrow,
  descripcion,
  tipo,
  mostrarEnviados,
  icono: Icono,
}: BonusSolicitudesPageProps) {
  useDocumentTitle(`Bonus · ${titulo}`);

  const listado = useSolicitudesBonus(tipo);
  const filas = listado.data?.solicitudes ?? [];
  const total = listado.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">{eyebrow}</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Icono className="size-6 text-brand-500" aria-hidden="true" />
          {titulo}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-ink-500">{descripcion}</p>
      </header>

      {listado.isError ? (
        <Alert variant="error" title="No se pudo cargar el historial">
          {listado.error instanceof Error
            ? listado.error.message
            : 'Revisa la conexión con EDSuite API.'}
        </Alert>
      ) : null}

      {listado.isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, indice) => (
            <Skeleton key={indice} className="h-40 w-full" />
          ))}
        </div>
      ) : null}

      {!listado.isLoading && filas.length === 0 ? (
        <Card className="p-8 text-center text-sm text-ink-500">
          Todavía no hay solicitudes registradas para esta operación.
        </Card>
      ) : null}

      {!listado.isLoading && filas.length > 0 ? (
        <>
          <p className="text-xs text-ink-400">
            {total} solicitudes registradas · la más reciente primero.
          </p>

          <div className="space-y-4">
            {filas.map((fila, indice) => (
              <Card key={fila.id} className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-xs text-ink-500">
                      {fila.request_url ?? '—'}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-400">
                      {formatDateTimeValue(fila.created_at)}
                    </p>
                  </div>
                  <Badge variant="neutral">#{indice + 1}</Badge>
                </div>

                <div className="mt-4 space-y-4">
                  {mostrarEnviados ? (
                    <CuerpoJson etiqueta="Datos enviados" valor={fila.request_body} />
                  ) : null}
                  <CuerpoJson etiqueta="Respuesta" valor={fila.response_body} />
                </div>
              </Card>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
